/**
 * Authenticated fetch utility — eliminates repeated raw-fetch boilerplate
 * in operations that can't use the typed SDK client (streaming, multipart, etc.).
 */

import { ArbiApiError } from './errors.js'

interface AuthHeaders {
  baseUrl: string
  accessToken: string
}

interface AuthFetchOptions extends AuthHeaders {
  /** URL path relative to baseUrl (e.g. '/v1/document/upload'). */
  path: string
  method?: string
  body?: string | FormData | ArrayBuffer | ReadableStream | Blob | null
  /** Extra headers to merge in. */
  headers?: Record<string, string>
}

/** Friendly fallbacks for status codes when the server body is empty or unparseable. */
const STATUS_FALLBACKS: Record<number, string> = {
  401: 'Token has expired. Please run: arbi login',
  403: 'Access denied. You may not have permission for this workspace.',
  404: 'Resource not found.',
  503: 'The selected LLM is not responding. Please retry or select another model.',
}

/**
 * Extract a human-readable error message from an HTTP response.
 *
 * Always tries to read the response body first — the server often includes
 * a specific `detail` string (e.g. "Parent message not found") that is more
 * useful than a generic status-code description.  Falls back to well-known
 * status messages only when the body is empty or unparseable.
 */
async function extractErrorMessage(res: Response): Promise<string> {
  // Try to parse error details from response body
  let bodyDetail: string | undefined
  try {
    const text = await res.text()
    if (text) {
      try {
        const json = JSON.parse(text) as { detail?: string; message?: string; error?: string }
        bodyDetail = json.detail || json.message || json.error || undefined
      } catch {
        // Not JSON — use raw text if it's reasonably short
        if (text.length < 200) bodyDetail = text
      }
    }
  } catch {
    // Ignore read errors
  }

  if (bodyDetail) return `Request failed (${res.status}): ${bodyDetail}`

  const fallback = STATUS_FALLBACKS[res.status]
  if (fallback) return fallback

  return `Request failed: ${res.status} ${res.statusText}`
}

/**
 * Make an authenticated fetch request with standard error handling.
 * Automatically sets Authorization header. Workspace keys are stored
 * server-side on the session — no client-side header needed.
 * Throws on non-ok responses with a human-readable message including server error details.
 */
export async function authenticatedFetch(options: AuthFetchOptions): Promise<Response> {
  const { baseUrl, accessToken, path, method, body, headers } = options

  const res = await fetch(`${baseUrl}${path}`, {
    method: method ?? (body ? 'POST' : 'GET'),
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...headers,
    },
    body,
  })

  if (!res.ok) {
    const message = await extractErrorMessage(res)
    throw new ArbiApiError(message, { status: res.status, statusText: res.statusText })
  }

  return res
}

export type { AuthHeaders }
